import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, filter, of, tap } from 'rxjs';
import { AlertService } from '../../../../shared/services/alert/alert.service';
import { graphql } from '../../graph.provider';
import {
  ACTION_DICTIONARY,
  GRAPHQL_ERROR_MESSAGES,
  OBJECT_DICTIONARY,
  PATH_ERROR_MESSAGES,
  generatePathErrorMessage,
} from './graphql-error-messages';

@Injectable()
export class GraphQlErrorInterceptor implements HttpInterceptor {
  private alertService = inject(AlertService);
  private router = inject(Router);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      filter(() => request.url === graphql),
      tap((event: HttpEvent<unknown>) => this.handleError(event)),
      catchError((event) => {
        this.handleError(event);
        return of(event);
      }),
    );
  }

  private handleError(event: HttpEvent<unknown>): void {
    if (event instanceof HttpResponse) {
      const response = event as HttpResponse<IGraphqlResponse>;
      if (this.isGraphQLError(response)) {
        this.handleGraphQLError(response);
        return;
      }
    }
    if (event instanceof HttpErrorResponse) {
      this.handleHttpError(event);
    }
  }

  private getErrorFromPath(error: IGraphqlError): string | undefined {
    const path = error.path;
    const pathMessage = PATH_ERROR_MESSAGES.find(
      (error) => error.path && JSON.stringify(error.path) == JSON.stringify(path),
    );
    if (pathMessage) {
      return generatePathErrorMessage(pathMessage);
    }
    const pathLast = path ? path[path.length - 1] : undefined;
    const pathSecondLast = path && path.length > 1 ? path[path.length - 2] : undefined;
    const actionMatches = Object.entries(ACTION_DICTIONARY)
      .filter((entry) => pathLast?.includes(entry[0]))
      .map((entry) => entry[1]);
    const action = actionMatches.length > 0 ? actionMatches[0] : undefined;
    const object = pathSecondLast ? OBJECT_DICTIONARY[pathSecondLast] : undefined;
    if (action && object) {
      return `Fout bij het ${action} van ${object}.`;
    }
    const queryObject = pathLast ? OBJECT_DICTIONARY[pathLast] : undefined;
    if (queryObject) {
      return `Fout bij het ophalen van ${queryObject}.`;
    }
    return undefined;
  }

  private handleGraphQLError(error: HttpResponse<IGraphqlResponse>): void {
    let title;
    const messages = [];
    const errors = error.body!.errors!;

    for (const error of errors) {
      // If validation errors, show all
      const validationErrors = Object.entries(error.extensions['validationErrors'] || {});
      if (validationErrors.length > 0) {
        messages.push(validationErrors.length > 1 ? 'Validatiefouten' : 'Validatiefout');
        validationErrors.forEach(([, message]) => messages.push(`"${message}"`));
      }

      // Map code as message
      const code = error.extensions['code'] as string;
      const codeMessage = GRAPHQL_ERROR_MESSAGES.find((error) => error.type === code)?.message;
      const codeMessageString = typeof codeMessage === 'function' ? codeMessage(error.message) : codeMessage;
      if (!title && codeMessageString) {
        title = codeMessageString;
      } else if (messages.length > 0 && codeMessageString) {
        messages.push(codeMessageString);
      }

      const pathMessageString = this.getErrorFromPath(error);

      if (!title && pathMessageString) {
        title = pathMessageString;
      } else if (messages.length > 0 && pathMessageString) {
        messages.push(pathMessageString);
      }

      // If no match, raw error from message
      if (messages.length === 0) {
        if (title) {
          messages.push(error.message);
        } else {
          messages.push(`Onbekende fout: "${error.message}"`);
        }
      }

      if (!title) {
        title = 'Fout';
      }
    }

    this.showAlert(error as unknown as HttpErrorResponse, title!, messages.join(', '));
  }

  private handleHttpError(error: HttpErrorResponse): void {
    if (error.status === HttpStatusCode.Unauthorized || error.status === HttpStatusCode.Forbidden) {
      this.router.navigate(['403']);
      throw error;
    }

    if (error.status === HttpStatusCode.TooManyRequests) {
      this.showAlert(error, 'Fout', 'Te veel verzoeken. Probeer het later opnieuw.');
      throw error;
    }

    if (error.status === HttpStatusCode.BadRequest) {
      this.showAlert(error, 'Ongeldig verzoek', error.message);
      throw error;
    }

    if (this.isGraphQLError(error as unknown as HttpResponse<IGraphqlResponse>)) {
      this.handleGraphQLError(error as unknown as HttpResponse<IGraphqlResponse>);
      throw error;
    }

    throw error;
  }

  private isGraphQLError(response: HttpResponse<IGraphqlResponse>): boolean {
    return Array.isArray(response.body?.errors) && response.body!.errors.length > 0;
  }

  private showAlert(response: HttpErrorResponse, title: string, message: string): void {
    const operationId = response.headers.get('OperationId') ?? undefined;
    const date = response.headers.get('Date');

    this.alertService.addError(title, message, operationId, date ? new Date(date) : undefined);
  }
}

interface IGraphqlResponse<T = unknown> {
  data?: T;
  errors?: IGraphqlError[];
}

interface IGraphqlError {
  message: string;
  locations?: { line: number; column: number }[];
  path?: string[];
  extensions: IGraphqlExtensions;
}

interface IGraphqlExtensions {
  [key: string]: unknown;
  code: string;
}
